import { supabase } from '@/integrations/supabase/client';
import {
  METRIC_SEED,
  METRIC_BOUNDS,
  isSnapshotMetric,
  type MetricKey,
  type FrameworkFamily,
} from '@/config/platform-metrics';

/**
 * Live platform metrics.
 *
 * WHY THIS EXISTS
 * Every number the site publishes used to be a compiled constant, so moving one
 * meant a PR and a rebuild. Counts that change weekly were structurally
 * guaranteed to lag. The numbers now live in the `platform_metrics` table and
 * this reads them through `get_public_platform_metrics()`.
 *
 * THE COMPILED CONSTANTS DID NOT GO AWAY
 * They are the seed: the value rendered on first paint, before any network
 * round trip, and the value kept if the fetch fails. That matters more than it
 * sounds. The alternative is rendering a skeleton or a zero where a number
 * belongs, and /templates already taught us what that costs, having published
 * "0 templates" for weeks because an RPC returned an error and the page
 * rendered the empty result faithfully.
 *
 * NOTHING UNBELIEVABLE GETS PUBLISHED
 * Bounds are checked twice, in the RPC and again here. A value that fails is
 * dropped and the seed stands. Belt and braces is deliberate: a wrong number on
 * a page whose whole argument is that our numbers survive inspection is a
 * different class of bug from a stale one.
 */

export type PlatformMetrics = Record<MetricKey, number> & {
  framework_families: FrameworkFamily[];
};

const CACHE_KEY = 'c2:platform-metrics:v1';
const CACHE_TTL_MS = 10 * 60 * 1000;

let inFlight: Promise<PlatformMetrics> | null = null;
let memo: { at: number; value: PlatformMetrics } | null = null;

const isPlausible = (key: MetricKey, n: unknown): n is number => {
  if (typeof n !== 'number' || !Number.isFinite(n) || n <= 0) return false;
  const bounds = METRIC_BOUNDS[key];
  if (!bounds) return true;
  return n >= bounds[0] && n <= bounds[1];
};

/** Merge a raw payload over the seed, discarding anything implausible. */
export function reconcile(raw: unknown): PlatformMetrics {
  const out = { ...METRIC_SEED } as PlatformMetrics;
  if (!raw || typeof raw !== 'object') return out;
  const src = raw as Record<string, unknown>;

  (Object.keys(METRIC_SEED) as MetricKey[]).forEach((key) => {
    if (key === ('framework_families' as MetricKey)) return;
    // Connector counts are a compiled snapshot. The RPC row is unpublished
    // because nothing writes `pushed` values. Accepting one would dress a
    // handwritten 116 as a fetched metric.
    if (isSnapshotMetric(key)) return;
    const candidate = src[key];
    if (isPlausible(key, candidate)) out[key] = candidate as never;
  });

  // Families are published as a breakdown of the headline framework count, so
  // they are only trustworthy if they still account for it. A rail that sums to
  // something other than the number printed one scroll above it is the exact
  // defect this whole system exists to prevent.
  const families = src.framework_families;
  if (Array.isArray(families) && families.length > 0) {
    const clean = families.filter(
      (f): f is FrameworkFamily =>
        !!f && typeof (f as FrameworkFamily).key === 'string' &&
        Number.isFinite((f as FrameworkFamily).count),
    );
    const total = clean.reduce((n, f) => n + f.count, 0);
    if (clean.length === families.length && total === out.frameworks_in_picker) {
      out.framework_families = clean as never;
    }
  }

  return out;
}

function readCache(): PlatformMetrics | null {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const { at, value } = JSON.parse(raw) as { at: number; value: unknown };
    if (Date.now() - at > CACHE_TTL_MS) return null;
    return reconcile(value);
  } catch {
    return null;
  }
}

export async function fetchPlatformMetrics(): Promise<PlatformMetrics> {
  if (memo && Date.now() - memo.at < CACHE_TTL_MS) return memo.value;
  if (inFlight) return inFlight;

  const cached = readCache();
  if (cached) {
    memo = { at: Date.now(), value: cached };
    return cached;
  }

  inFlight = (async () => {
    try {
      const { data, error } = await supabase.rpc('get_public_platform_metrics');
      if (error) throw error;
      const value = reconcile(data);
      memo = { at: Date.now(), value };
      try {
        sessionStorage.setItem(CACHE_KEY, JSON.stringify({ at: Date.now(), value: data }));
      } catch {
        /* private mode, quota, blocked storage: the memo still holds */
      }
      return value;
    } catch {
      // Seed values are correct as of the last release, so failing back to them
      // is a stale page rather than a wrong one.
      const value = { ...METRIC_SEED } as PlatformMetrics;
      memo = { at: Date.now(), value };
      return value;
    } finally {
      inFlight = null;
    }
  })();

  return inFlight;
}
